import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { endpoints } from '@/services/endpoints'
import { createSharedSseConnection, sseEnabled } from '@/services/sse'
import { usePermission } from '@/features/auth/permissions'
import { useProfile } from '@/features/auth/mutations'
import {
  createShiftHandover,
  deleteShiftHandover,
  fetchShiftHandover,
  fetchShiftHandovers,
  toHandover,
  updateShiftHandover,
} from './api'
import { subscribeToShiftHandoverList } from './liveStream'

/**
 * Who may do what with a handover.
 *
 * Every route accepts `shift_handovers.manage` OR `.prepare`, so either opens
 * the screen. Changing one is where they differ: `manage` edits and deletes any
 * handover in its scope, `prepare` only the ones its holder created
 * (`authorize_handover_write`). `created_by` is on every row, so — unlike the
 * log book — this client CAN tell whose is whose, and does: it reads its own id
 * from `/users/me`, because the session read carries none.
 *
 * Scope is not checked here. A `manage` grant at one plant still 403s on
 * another plant's handover; the server's refusal is shown rather than guessed.
 */
export function useShiftHandoverAccess() {
  const manage = usePermission('shift_handovers.manage')
  const prepare = usePermission('shift_handovers.prepare')
  const granted = manage.granted || prepare.granted
  const profile = useProfile({ enabled: granted })
  const myId = profile.data?.id ?? null

  return {
    isPending: manage.isPending || prepare.isPending,
    granted,
    isRoot: manage.isRoot,
    canChange: (handover) =>
      Boolean(handover) &&
      (manage.granted || (prepare.granted && myId != null && handover.createdBy === myId)),
    isMine: (handover) => Boolean(handover) && myId != null && handover.createdBy === myId,
  }
}

/**
 * One page of handovers.
 *
 * `placeholderData` keeps the table on screen while a filter lands. Liveness
 * comes from `useShiftHandoverListStream`, not from polling.
 */
export const useShiftHandovers = (params, { enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.shiftHandoverList(params),
    queryFn: ({ signal }) => fetchShiftHandovers(params, signal),
    placeholderData: (previous) => previous,
    enabled,
    staleTime: 60_000,
  })

/**
 * One handover.
 *
 * `staleTime: 0` because the edit is a full replace of the notes and all three
 * lists — a form seeded from a stale copy writes back whatever links that copy
 * happened to hold. `gcTime: 0` because `useShiftHandoverStream` writes into
 * this key and lives exactly as long as this hook's observer.
 */
export const useShiftHandover = (id, { enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.shiftHandover(id),
    queryFn: ({ signal }) => fetchShiftHandover(id, signal),
    enabled: enabled && Boolean(id),
    staleTime: 0,
    gcTime: 0,
  })

/**
 * Keep the list live. Opt-in: mounting this is what makes a list live.
 *
 * @returns {{status: 'idle'|'connecting'|'live'|'retrying'|'failed'}}
 */
export function useShiftHandoverListStream({ enabled = true } = {}) {
  const client = useQueryClient()
  const [status, setStatus] = useState('idle')

  useEffect(() => {
    if (!enabled || !sseEnabled()) return undefined
    return subscribeToShiftHandoverList(
      () => client.invalidateQueries({ queryKey: ['shiftHandovers', 'list'] }),
      setStatus,
    )
  }, [enabled, client])

  return { status }
}

/**
 * Keep one handover live — `GET /shifts/handovers/{id}/stream`.
 *
 * The event carries the full `ShiftHandoverOut`, so it is applied rather than
 * refetched, unless an older copy would overwrite a newer one. An event with no
 * row (the server pops its payload, so a second listener can be handed nothing)
 * falls back to a refetch. Not shared and not lingered: one open handover, one
 * socket, closed with it.
 *
 * Linked items do not push. A work order's status changing does not fire this
 * stream — the trigger is on `shift_handovers` alone — so the summaries shown
 * are as of the last change to the handover itself or the last open.
 */
export function useShiftHandoverStream(id, { enabled = true } = {}) {
  const client = useQueryClient()
  const [status, setStatus] = useState('idle')

  useEffect(() => {
    if (!id || !enabled || !sseEnabled()) return undefined

    const connection = createSharedSseConnection({
      path: endpoints.shiftHandovers.detailStream(id),
      lingerMs: 0,
      onEvent: (event) => {
        if (!event?.id) {
          client.invalidateQueries({ queryKey: queryKeys.shiftHandover(id) })
        } else {
          const incoming = toHandover(event)
          client.setQueryData(queryKeys.shiftHandover(id), (previous) =>
            previous?.updatedAt && incoming.updatedAt && previous.updatedAt > incoming.updatedAt
              ? previous
              : incoming,
          )
        }
        /* The list row behind it moved too — its counts at least. */
        client.invalidateQueries({ queryKey: ['shiftHandovers', 'list'] })
      },
      onOpen: ({ reconnected }) => {
        if (reconnected) client.invalidateQueries({ queryKey: queryKeys.shiftHandover(id) })
      },
      onStatus: setStatus,
    })

    return connection.subscribe()
  }, [id, enabled, client])

  return { status }
}

/**
 * Create, update and delete. None retried: every way these fail is an answer —
 * not rostered, a duplicate, a link at the wrong place — and asking again gets
 * the same one. A create that succeeded and failed to report would also make the
 * retry a duplicate, which the server refuses anyway.
 *
 * Each sweeps the whole `['shiftHandovers']` root: the row has to appear under
 * whatever filters are on, and an open detail has to show what was saved.
 */
function useHandoverWrite(mutationFn) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.shiftHandovers }),
  })
}

export const useCreateShiftHandover = () => useHandoverWrite(createShiftHandover)
export const useUpdateShiftHandover = () => useHandoverWrite(updateShiftHandover)
export const useDeleteShiftHandover = () => useHandoverWrite(deleteShiftHandover)
